// Configuration API
const API_BASE_URL = import.meta.env.VITE_REACT_APP_API_EP_URI;

export interface CycleInput {
  name?: string;
  short_name?: string;
  branches?: string[];
  binome_only_branches?: string[];
  solo_fallback?: Record<string, string>;
  default_dates_from_sessions?: { start: string; end: string } | null;
}

export interface StoryInput {
  code?: string;
  description?: string;
  story_points?: number;
  branches?: string[];
  cycle_id?: number;
}

async function callApi(path: string, method: 'POST' | 'PUT' | 'DELETE', accessToken: string, body?: any): Promise<any> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(json.message || `HTTP error! status: ${response.status}`);
  }
  return json;
}

// Admin Cycles : cycles calés sur des branches, leurs US/TS et leurs points (voir
// api/main/resources/admin_cycle_resource.py)
class AdminCycleService {
  createCycle(data: CycleInput, token: string) { return callApi('/admin/cycles', 'POST', token, data); }
  updateCycle(id: number, data: CycleInput, token: string) { return callApi(`/admin/cycles/${id}`, 'PUT', token, data); }
  deleteCycle(id: number, token: string) { return callApi(`/admin/cycles/${id}`, 'DELETE', token); }
  reorderCycles(cycleIds: number[], token: string) { return callApi('/admin/cycles/order', 'POST', token, { cycle_ids: cycleIds }); }
  setDates(id: number, dates: { start_date: string | null; end_date: string | null }, token: string) {
    return callApi(`/admin/cycles/${id}/dates`, 'PUT', token, dates);
  }
  splitCycle(id: number, data: { from_story_id: number; name: string; short_name: string }, token: string) {
    return callApi(`/admin/cycles/${id}/split`, 'POST', token, data);
  }
  addStory(cycleId: number, data: StoryInput, token: string) { return callApi(`/admin/cycles/${cycleId}/stories`, 'POST', token, data); }
  updateStory(id: number, data: StoryInput, token: string) { return callApi(`/admin/stories/${id}`, 'PUT', token, data); }
  deleteStory(id: number, token: string) { return callApi(`/admin/stories/${id}`, 'DELETE', token); }
  reorderStories(cycleId: number, storyIds: number[], token: string) {
    return callApi(`/admin/cycles/${cycleId}/stories/order`, 'POST', token, { story_ids: storyIds });
  }
}

export const adminCycleService = new AdminCycleService();

// Clé du rapport de tests envoyée par la CI pour un code de story : 'TP.US1' -> 'us_1',
// 'PREPA.TS2.1' -> 'ts_2_1', 'PRJ.US2_TS1' -> 'us_2_ts1' (même règle que la page des résultats)
export function ciKeyForCode(code: string): string {
  const tail = code.includes('.') ? code.split('.').slice(1).join('_') : code;
  return tail.toLowerCase().replace(/([a-z])(\d)/g, '$1_$2').replace(/_ts_/g, '_ts');
}
